<script setup lang="ts">
import type {NuxtError} from "#app";

const props = defineProps({
  error: Object as () => NuxtError
});

const localePath = useLocalePath();
const { t } = useI18n();

const statusCode = computed(() => props.error?.statusCode ?? 404);
const is404 = computed(() => statusCode.value === 404);

// Without this the fallback title echoes the unresolved path back at the
// visitor, and the description falls through to the English site default.
useSeoMeta({
  title: () => (is404.value ? t('404') : t('errorGenericTitle')),
  description: () => t('clickAnywhereToGoHome'),
  robots: 'noindex, follow',
});

// clearError resets the error state before leaving, and localePath keeps
// the visitor in their current locale instead of dumping them on the Greek home page.
const goHome = () => clearError({redirect: localePath('/')});
</script>

<template>
  <div class="error-container" @click="goHome">
    <!-- Inline art: the error page must render even when the CDN or server is down -->
    <svg class="error-art" viewBox="0 0 480 300" role="img"
         :aria-label="is404 ? $t('404') : $t('errorGenericTitle')">
      <circle class="art-sun" cx="404" cy="64" r="26"/>

      <polygon class="art-peak-back" points="0,264 110,120 190,204 250,264"/>
      <polygon class="art-peak-back" points="270,264 350,168 480,264"/>
      <polygon class="art-peak-main" points="110,264 272,58 434,264"/>
      <polygon class="art-snow"
               points="243,95 272,58 301,95 291,88 281,97 272,89 262,97 252,88"/>

      <!-- summit flag marking the "altitude" of this page -->
      <line class="art-pole" x1="272" y1="58" x2="272" y2="12"/>
      <rect class="art-flag" x="272" y="12" width="58" height="21" rx="3"/>
      <text class="art-flag-text" x="301" y="27" text-anchor="middle">{{ statusCode }}m</text>

      <!-- the trail that led you here -->
      <path class="art-trail" d="M30 292 C 110 268, 150 296, 214 276 S 330 288, 388 262"/>
    </svg>

    <h1>{{ is404 ? $t('404') : $t('errorGenericTitle') }}</h1>
    <h2>
      <!-- The whole page is clickable, but only a link puts the way out in the
           tab order and announces it. -->
      <nuxt-link :to="localePath('/')" class="home-link" @click.prevent.stop="goHome">
        {{ $t('clickAnywhereToGoHome') }}
      </nuxt-link>
    </h2>
  </div>
</template>

<style scoped lang="sass">
// Inherits the heading's look so the page is unchanged visually.
.home-link
  color: inherit
  text-decoration: none

.error-container
  display: flex
  justify-content: center
  align-items: center
  flex-direction: column
  width: 100vw
  min-height: 100vh
  cursor: pointer
  margin: 0
  padding: 2rem
  text-align: center

.error-art
  width: min(420px, 82vw)
  height: auto
  margin-bottom: 1.5rem

h1
  font-size: 2rem
  margin: 0 0 0.75rem

h2
  font-size: 1.1rem
  font-weight: 500
  opacity: 0.6
  margin: 0

// ─── Artwork colors ──────────────────────────────────────────────
.art-sun
  fill: #f7c948

.art-peak-back
  fill: #a5dcc4

.art-peak-main
  fill: $color-primary

.art-snow
  fill: white

.art-pole
  stroke: rgba(0, 0, 0, 0.65)
  stroke-width: 3
  stroke-linecap: round

.art-flag
  fill: #e5534b

.art-flag-text
  fill: white
  font-family: 'Montserrat', sans-serif
  font-size: 13px
  font-weight: 800

.art-trail
  fill: none
  stroke: rgba(0, 0, 0, 0.25)
  stroke-width: 3
  stroke-dasharray: 1 10
  stroke-linecap: round

.dark-mode .art-sun
  fill: #e8e6df

.dark-mode .art-peak-back
  fill: #2e5c4a

.dark-mode .art-peak-main
  fill: #3f8a6d

.dark-mode .art-snow
  fill: #ebf4f1

.dark-mode .art-pole
  stroke: rgba(255, 255, 255, 0.7)

.dark-mode .art-trail
  stroke: rgba(255, 255, 255, 0.3)

@media (max-width: 480px)
  h1
    font-size: 1.5rem
  h2
    font-size: 1rem
</style>
